import classNames from "classnames";
import React, { FunctionComponent, ReactElement, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CartValidPromoCode from "@Components/atoms/cart/CartValidPromoCode";
import useCartContext from "@Hooks/useCartContext";
import { ReactComponent as IconX } from "@Icons/cross.svg";
import { Price } from "@Types/api/price";
import { setDataLayerTrack } from "@Utils/track";
import "./style.scss";

interface Props {
  disabled: boolean;
  errorMessage?: string;
  onUpdate: (promoCode: string | undefined) => void;
  promoCode?: string;
  promoTotal: Price;
}

interface ContainerProps {
  disabled: boolean;
  errorMessage: string | undefined;
  onUpdatePromoCode: (promoCode: string | undefined) => void;
}

export const CartPromoCode: FunctionComponent<Props> = ({
  disabled,
  errorMessage,
  onUpdate,
  promoCode,
  promoTotal,
}): ReactElement => {
  const intl = useIntl();
  const [input, setInput] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isFocus, setIsFocus] = useState<boolean>(false);

  const isError = errorMessage !== undefined && errorMessage !== "";
  const isSuccess =
    promoCode !== "" &&
    promoCode !== undefined &&
    (errorMessage === "" || errorMessage === undefined);

  const handleSubmit = useCallback(() => {
    if (!disabled) {
      setIsFocus(false);
      onUpdate(input.toUpperCase());
    }
  }, [disabled, onUpdate, input]);

  const handleDismiss = () => {
    if (!isError) {
      setDataLayerTrack("Coupon Removed", {
        couponId: promoCode,
        discount: promoTotal.cents,
      });
    }
    setInput("");
    onUpdate(undefined);
  };

  const handleKeyPress = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        handleSubmit();
      } else {
        setIsFocus(true);
      }
    },
    [handleSubmit]
  );

  useEffect(() => {
    setIsEditing(input !== "" && !isSuccess && isFocus);
  }, [input, isFocus, isSuccess]);

  return (
    <div className="CartPromoCode">
      {isSuccess && promoCode ? (
        <CartValidPromoCode
          disabled={disabled}
          onDismiss={handleDismiss}
          promoCode={promoCode}
          promoTotal={promoTotal}
        />
      ) : (
        <div className="CartPromoCode-form">
          <div className="CartPromoCode-input-wrapper">
            <input
              className={classNames("CartPromoCode-input", {
                "CartPromoCode-input-isEditing": isEditing,
                "CartPromoCode-input-isError": isError,
              })}
              disabled={disabled}
              onChange={(e) => setInput(e.target.value)}
              onFocus={() => setIsFocus(true)}
              onKeyPress={handleKeyPress}
              placeholder={intl.formatMessage({ id: "cheerz_CartPromoCode_add" })}
              required={true}
              tabIndex={0}
              type="text"
              value={input}
            />
            {isError && !!input && (
              <IconX className="CartPromoCode-input-icons-remove" onClick={handleDismiss} />
            )}
          </div>
          {isEditing && (
            <button
              className="CartPromoCode-confirmButton"
              disabled={disabled}
              onClick={handleSubmit}
            >
              <FormattedMessage id="cheerz_CartPromoCode_validate" />
            </button>
          )}
        </div>
      )}

      {errorMessage && <div className="CartPromoCode-errorMessage">{errorMessage}</div>}
    </div>
  );
};

const CartPromoCodeContainer: FunctionComponent<ContainerProps> = ({
  disabled,
  errorMessage,
  onUpdatePromoCode,
}) => {
  const { cart } = useCartContext();

  return (
    <CartPromoCode
      disabled={disabled}
      errorMessage={errorMessage}
      onUpdate={onUpdatePromoCode}
      promoCode={cart.promoCode}
      promoTotal={cart.promoTotal}
    />
  );
};

export default CartPromoCodeContainer;
